import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand, BrandText } from "@/components/Brand";
import { toast } from "sonner";
import { ShieldStar, Lock, Translate } from "@phosphor-icons/react";

export default function Login() {
    const { login, verifyMfa, loginWithGoogle } = useAuth();
    const { t, toggle, nextLangLabel } = useI18n();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [challenge, setChallenge] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try {
            const r = await login(email, password);
            if (r.mfa) setChallenge(r.challenge_token);
            else { toast.success(t("success")); nav("/app"); }
        } catch (e) {
            const d = e.response?.data?.detail;
            setErr(typeof d === "string" ? d : t("error"));
        } finally { setBusy(false); }
    };

    const submitMfa = async (e) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try {
            await verifyMfa(challenge, code);
            toast.success(t("success"));
            nav("/app");
        } catch (e) {
            setErr(t("error"));
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            {/* Left: form */}
            <div className="flex flex-col p-8 sm:p-12 justify-between">
                <div className="flex items-center justify-between">
                    <Brand to="/" size="md" tagline="GOV · SECURE" dataTestId="login-brand" />
                    <Button variant="ghost" size="sm" onClick={toggle} data-testid="login-lang-toggle">
                        <Translate size={16} weight="bold" className="me-1" />{nextLangLabel()}
                    </Button>
                </div>

                <div className="max-w-md w-full self-center">
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary mb-3">· {t("signIn")}</div>
                    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" data-testid="login-title">{t("loginTitle")}</h1>
                    <p className="text-sm text-muted-foreground mb-8">{t("loginSubtitle")}</p>

                    {!challenge ? (
                        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                            <div>
                                <Label htmlFor="email" className="text-xs tracking-widest uppercase font-bold">{t("email")}</Label>
                                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="login-email" placeholder="you@utal.gov" />
                            </div>
                            <div>
                                <Label htmlFor="pw" className="text-xs tracking-widest uppercase font-bold">{t("password")}</Label>
                                <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" data-testid="login-password" />
                            </div>
                            {err && <div className="text-sm text-primary" data-testid="login-error">{err}</div>}
                            <Button type="submit" disabled={busy} className="w-full h-11 rounded-sm bg-primary hover:bg-primary/90" data-testid="login-submit">
                                <Lock size={16} weight="bold" className="me-2" />{busy ? "…" : t("login")}
                            </Button>

                            <div className="text-xs text-muted-foreground pt-4 border-t border-border">
                                Access to this platform is by invitation only. Sign in with the email + password from your invite. Google Sign-In is disabled while the platform is in restricted access mode.
                            </div>
                        </form>
                    ) : (
                        <form onSubmit={submitMfa} className="space-y-5" data-testid="mfa-form">
                            <h2 className="text-xl font-semibold">{t("mfaTitle")}</h2>
                            <p className="text-sm text-muted-foreground">{t("mfaSubtitle")}</p>
                            <Input inputMode="numeric" pattern="[0-9]*" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} className="h-14 text-center text-3xl tracking-[0.6em] mono" data-testid="mfa-code" />
                            {err && <div className="text-sm text-primary">{err}</div>}
                            <Button type="submit" disabled={busy || code.length < 6} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="mfa-submit">
                                {busy ? "…" : t("verify")}
                            </Button>
                        </form>
                    )}
                </div>

                <div className="text-xs text-muted-foreground mono">
                    © <BrandText className="text-xs" /> · {new Date().getFullYear()}
                </div>
            </div>

            {/* Right: art panel */}
            <div className="hidden lg:block relative bg-[#101012] text-white overflow-hidden">
                <div className="grain" />
                <div
                    className="absolute inset-0 opacity-40"
                    style={{
                        backgroundImage: "url(https://images.pexels.com/photos/29840317/pexels-photo-29840317.jpeg?auto=compress&cs=tinysrgb&w=1400)",
                        backgroundSize: "cover", backgroundPosition: "center",
                        maskImage: "linear-gradient(to bottom, black 10%, transparent 100%)",
                    }}
                />
                <div className="relative z-10 h-full flex flex-col justify-end p-12">
                    <div className="text-[11px] tracking-[0.4em] text-primary mb-4">· ED25519 · JWS · TAMPER-EVIDENT</div>
                    <h2 className="text-3xl font-bold leading-tight max-w-md">{t("tagline")}</h2>
                    <p className="mt-4 text-white/70 text-sm max-w-md">{t("subtagline")}</p>
                </div>
            </div>
        </div>
    );
}
